import React, { useMemo } from 'react';
import { GradeBadge } from './Badges';
import { groupScansBySite, Site } from '../utils/sites';
import { formatDate } from '../utils/date';
import { useIsMobile } from '../hooks/useMediaQuery';
import { T, isUngraded } from '../designTokens';
import type { ScanResult } from '../utils/types';

// ---------------------------------------------------------------------------
// The customer's sites, one row per page they audit: the latest grade, the
// change since the audit before it, and the two things they come back to do
// (read the report, audit again). Replaces the run-by-run "Recent Scans"
// table, which listed acme.com three times and said nothing about direction.
// ---------------------------------------------------------------------------


function ScoreChange({ change }: { change: number | null }) {
  if (change === null || change === 0) return null;
  const up = change > 0;
  const points = Math.abs(change);
  return (
    <span
      title={`${points} point${points === 1 ? '' : 's'} ${up ? 'higher' : 'lower'} than the previous audit`}
      style={{
        fontFamily: T.fontMono,
        fontSize: '12px',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        color: up ? T.green700 : T.red700,
      }}
    >
      <span aria-hidden="true">{up ? '+' : '−'}{points}</span>
      <span className="sr-only">
        {`${up ? 'up' : 'down'} ${points} since the previous audit`}
      </span>
    </span>
  );
}

function SiteRow({
  site,
  first,
  isMobile,
  onView,
  onAuditAgain,
}: {
  site: Site;
  first: boolean;
  isMobile: boolean;
  onView: (scan: ScanResult) => void;
  onAuditAgain?: (url: string) => void;
}) {
  const { latest } = site;
  const running = latest.status === 'pending' || latest.status === 'running';
  const failed = latest.status === 'failed';
  const locked = latest.licenseStatus === 'locked';
  const audits = `${site.auditCount} audit${site.auditCount === 1 ? '' : 's'}`;
  const actionHeight = isMobile ? '44px' : '36px';

  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto auto',
        alignItems: 'center',
        gap: isMobile ? '12px' : '24px',
        padding: isMobile ? '16px' : '16px 20px',
        borderTop: first ? 'none' : `1px solid ${T.gray100}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: T.fontMono,
            fontSize: '14px',
            fontWeight: 600,
            color: T.gray800,
            overflowWrap: 'anywhere',
          }}
        >
          {site.label}
        </div>
        <div style={{ marginTop: '4px', fontSize: '13px', color: T.gray600 }}>
          {running ? 'Audit running' : `Last audited ${formatDate(latest.startedAt)}`} · {audits}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {running ? null : failed ? (
          <span style={{ fontSize: '13px', color: T.gray600 }}>Did not finish</span>
        ) : (
          <>
            <GradeBadge score={isUngraded(latest.overallStatus) ? null : latest.score} />
            <ScoreChange change={site.scoreChange} />
            {locked && (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '9999px',
                  backgroundColor: T.gray100,
                  color: T.gray600,
                  fontFamily: T.fontMono,
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                Locked
              </span>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
        <button
          type="button"
          className="site-row__primary"
          onClick={() => onView(latest)}
          aria-label={`${locked ? 'Unlock' : 'View'} the report for ${site.label}`}
          style={{
            flex: isMobile ? 1 : undefined,
            minHeight: actionHeight,
            padding: '0 14px',
            fontSize: '14px',
            fontWeight: 600,
            color: locked ? T.cobalt700 : T.gray800,
            backgroundColor: T.white,
            border: `1px solid ${locked ? T.cobalt200 : T.gray300}`,
            borderRadius: '6px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {running ? 'View progress' : locked ? 'Unlock report' : 'View report'}
        </button>
        {onAuditAgain && (
          <button
            type="button"
            className="site-row__secondary"
            onClick={() => onAuditAgain(site.url)}
            disabled={running}
            aria-label={`Audit ${site.label} again`}
            style={{
              flex: isMobile ? 1 : undefined,
              minHeight: actionHeight,
              padding: '0 10px',
              fontSize: '14px',
              fontWeight: 600,
              color: running ? T.gray400 : T.cobalt600,
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: running ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Audit again
          </button>
        )}
      </div>
    </li>
  );
}

export default function YourSites({
  scans,
  onView,
  onAuditAgain,
}: {
  scans: ScanResult[];
  onView: (scan: ScanResult) => void;
  /** Absent at a zero balance, where the form is replaced by the credit offer. */
  onAuditAgain?: (url: string) => void;
}) {
  const isMobile = useIsMobile();
  const sites = useMemo(() => groupScansBySite(scans), [scans]);

  return (
    <section aria-labelledby="your-sites-heading">
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
          <h2
            id="your-sites-heading"
            style={{
              margin: 0,
              fontFamily: T.fontDisplay,
              fontSize: '20px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: T.gray800,
            }}
          >
            Your sites
          </h2>
          <span style={{ fontFamily: T.fontMono, fontSize: '12px', fontVariantNumeric: 'tabular-nums', color: T.gray600 }}>
            {sites.length} site{sites.length === 1 ? '' : 's'} · {scans.length} audit{scans.length === 1 ? '' : 's'}
          </span>
        </div>
        <a href="#/reports" className="text-link" style={{ fontSize: '14px', fontWeight: 600, color: T.cobalt600 }}>
          All reports
        </a>
      </div>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          backgroundColor: T.white,
          border: `1px solid ${T.gray200}`,
          borderRadius: '10px',
          overflow: 'hidden',
        }}
      >
        {sites.map((site, i) => (
          <SiteRow key={site.key} site={site} first={i === 0} isMobile={isMobile} onView={onView} onAuditAgain={onAuditAgain} />
        ))}
      </ul>
    </section>
  );
}
